import { LitElement, html, css, nothing } from 'lit';
import { PRINT_STYLES } from './print-styles.js';
import { repeat } from 'lit/directives/repeat.js';
import { StoreMixin, store } from '../store.js';
import { api } from '../api.js';
import { navigate } from '../router.js';
import { apiParams } from '../lib/filters.js';
import { CONFIDENCE_TIERS, STATUS_STATES } from '../lib/palette.js';
import { fillFor } from '../lib/patterns.js';
import {
  PERSON_PARAM_KEYS,
  allocationExportRows,
  allocationScaleMax,
  basisSentence,
  billableExportRows,
  billableScaleMax,
  billableSentence,
  billableState,
  billableStateLabel,
  contributorExportRows,
  denominatorLabel,
  foldContributors,
  foldSegments,
  timeOffSentence,
  weekStack,
  personBillableColumns,
  personContributorColumns,
  personWeekColumns,
} from '../lib/person.js';
import { withFormats } from '../lib/table.js';
import {
  hours as formatHours,
  percent,
  count,
  weekLabel,
  mediumDate,
  longDate,
  humanize,
} from '../lib/format.js';

const fmt = { hours: formatHours, percent, count, mediumDate, week: weekLabel };
const exact = (h) => formatHours(h, { exact: true });
const TIER_META = new Map(CONFIDENCE_TIERS.map((t) => [t.key, t]));
const STATE_META = new Map(STATUS_STATES.map((s) => [s.key, s]));

/**
 * The individual view (PRD 6.6, 7.2; TRD 10.2; Phase 32): one person's weeks. One response,
 * `GET /forecast/people/:id`, read in two halves that never touch:
 *
 * The **allocation** half is this tool's metric — the run's hours on the person per week,
 * stacked by the projects, retainers and deals behind them and their non-project load, against
 * the frozen capacity line; the capacity breakdown (contracted, time off, non-project) that made
 * the line; and the contributors listed with what each put on the person. Every week's cell is
 * the heatmap's cell for this person's row, and the card adds no arithmetic to it.
 *
 * The **billable** half is Workamajig's metric — the hours its report counts as billable over
 * the trailing complete weeks, divided by the week's net hours — in its own card, its own chart,
 * on its own axis, read against Workamajig's own band in neutral ink. The two are never on one
 * chart and never in one status colour (PRD 6.6; O-11): the word beside the billable figure says
 * "in the band", not green.
 */
class PersonView extends StoreMixin(LitElement) {
  static properties = {
    personId: { type: String, attribute: 'person-id' },
    _data: { state: true },
    _loading: { state: true },
    _error: { state: true },
    _notFound: { state: true },
    _hover: { state: true },
    _billableHover: { state: true },
    _weeksOpen: { state: true },
    _contributorsOpen: { state: true },
    _billableOpen: { state: true },
  };

  constructor() {
    super();
    this.personId = null;
    this._data = null;
    this._loading = false;
    this._error = null;
    this._notFound = null;
    this._hover = null;
    this._billableHover = null;
    this._weeksOpen = false;
    this._contributorsOpen = false;
    this._billableOpen = false;
    this._requestKey = null;
    this._fold = null;
    this._foldFor = null;
  }

  static styles = [
    PRINT_STYLES,
    css`
      :host {
        display: block;
      }
      .card {
        background: var(--surface);
        border: 1px solid var(--border-soft);
        border-radius: var(--radius-card);
        box-shadow: var(--shadow-card);
        padding: 20px 24px 18px;
      }
      .card + .card {
        margin-top: 18px;
      }
      .head {
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
        gap: 6px 18px;
        margin-bottom: 14px;
      }
      .eyebrow {
        font-family: var(--font-eyebrow);
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.16em;
        text-transform: uppercase;
        color: var(--ink-muted);
      }
      h2 {
        margin: 2px 0 0;
        font-size: 20px;
        line-height: 1.2;
        color: var(--ink-strong);
      }
      .sub {
        color: var(--ink-muted);
        font-size: 12.5px;
      }
      .kpis {
        margin-left: auto;
        display: flex;
        gap: 18px;
        flex-wrap: wrap;
      }
      .kpi {
        min-width: 88px;
      }
      .kpi .label {
        font-family: var(--font-eyebrow);
        font-size: 9.5px;
        font-weight: 700;
        letter-spacing: 0.14em;
        text-transform: uppercase;
        color: var(--ink-muted);
      }
      .kpi .value {
        font-size: 16px;
        font-weight: 600;
        color: var(--ink-strong);
        font-variant-numeric: tabular-nums;
      }
      .kpi .value small {
        font-size: 11.5px;
        font-weight: 500;
        color: var(--ink-muted);
      }
      .grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
        gap: 16px 24px;
        margin: 14px 0 6px;
      }
      .block h3 {
        margin: 0 0 6px;
        font-family: var(--font-eyebrow);
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.14em;
        text-transform: uppercase;
        color: var(--ink-muted);
      }
      .block p,
      p.sub {
        margin: 0 0 4px;
        font-size: 13px;
        line-height: 1.45;
        color: var(--ink);
      }
      .block p.muted,
      p.muted {
        color: var(--ink-muted);
        font-size: 12.5px;
      }
      .chip {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        font-size: 11.5px;
        font-weight: 600;
        color: var(--ink-strong);
        background: var(--surface-2);
        border: 1px solid var(--border-soft);
        border-radius: var(--radius-pill);
        padding: 1px 8px;
        vertical-align: middle;
      }
      .chip .marker {
        font-family: var(--font-eyebrow);
        color: var(--ink-muted);
      }
      .plain-list,
      .contrib-list,
      .cap-list {
        list-style: none;
        margin: 0;
        padding: 0;
      }
      .contrib-list li {
        display: grid;
        grid-template-columns: minmax(0, 1fr) 64px;
        gap: 2px 10px;
        align-items: baseline;
        padding: 5px 0;
        border-top: 1px solid var(--border-soft);
        font-size: 12.5px;
      }
      .contrib-list li:first-child {
        border-top: 0;
      }
      .contrib-list .name {
        display: flex;
        align-items: center;
        gap: 8px;
        min-width: 0;
      }
      .contrib-list .swatch {
        flex: 0 0 auto;
        width: 10px;
        height: 10px;
        border-radius: 2px;
      }
      .contrib-list .label {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .contrib-list .detail {
        grid-column: 1 / -1;
        color: var(--ink-muted);
        font-size: 12px;
        padding-left: 18px;
      }
      .contrib-list .hours {
        text-align: right;
        font-variant-numeric: tabular-nums;
        font-weight: 600;
        color: var(--ink-strong);
      }
      .contrib-list .hours small {
        display: block;
        font-weight: 500;
        color: var(--ink-muted);
        font-size: 11px;
      }
      .cap-list li {
        display: grid;
        grid-template-columns: minmax(0, 1fr) max-content;
        gap: 0 12px;
        padding: 3px 0;
        font-size: 12.5px;
        border-top: 1px solid var(--border-soft);
      }
      .cap-list li:first-child {
        border-top: 0;
      }
      .cap-list li.total {
        font-weight: 600;
        color: var(--ink-strong);
      }
      .cap-list .figure {
        font-variant-numeric: tabular-nums;
      }
      .off-list li {
        padding: 3px 0;
        font-size: 12.5px;
        color: var(--ink-muted);
      }
      a.link,
      button.link {
        color: var(--link);
        text-decoration: none;
        font: inherit;
        font-weight: 600;
        background: none;
        border: 0;
        padding: 0;
        cursor: pointer;
        text-align: left;
      }
      a.link:hover,
      button.link:hover {
        text-decoration: underline;
      }
      .chart {
        margin: 4px 0 10px;
      }
      .legend-row {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 6px 18px;
        margin-top: 6px;
        font-size: 12px;
        color: var(--ink-muted);
      }
      .line-key,
      .band-key {
        display: inline-flex;
        align-items: center;
        gap: 6px;
      }
      .band-key .swatch {
        width: 18px;
        height: 10px;
        background: var(--surface-sunken);
        border: 1px solid var(--border-soft);
      }
      .state {
        padding: 28px 0;
        color: var(--ink-muted);
        font-size: 13px;
      }
      .state.error {
        color: var(--status-overloaded-ink);
      }
      .note {
        margin: 6px 0 0;
        font-size: 12px;
        color: var(--ink-muted);
      }
      .reference {
        display: inline-flex;
        align-items: center;
        margin-left: 8px;
        font-size: 10px;
        letter-spacing: 0.08em;
        color: var(--ink-muted);
        border: 1px dashed var(--border);
        border-radius: var(--radius-pill);
        padding: 0 7px;
      }
      details.table {
        margin-top: 12px;
        border-top: 1px solid var(--border-soft);
        padding-top: 8px;
      }
      details.table summary {
        cursor: pointer;
        font-size: 12.5px;
        font-weight: 600;
        color: var(--ink-muted);
        list-style: none;
      }
      details.table summary::-webkit-details-marker {
        display: none;
      }
      details.table summary::before {
        content: '▸ ';
      }
      details.table[open] summary::before {
        content: '▾ ';
      }
      details.table data-table {
        margin-top: 10px;
      }
    `,
  ];

  connectedCallback() {
    super.connectedCallback();
    this._unsubscribeFetch = store.subscribe(() => this._maybeFetch());
    this._maybeFetch();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._unsubscribeFetch?.();
  }

  updated(changed) {
    if (changed.has('personId')) this._maybeFetch();
  }

  /** The request this page wants: the person, for the run, window and pipeline mode in the bar. */
  _params() {
    const { filters, ready } = this.appState;
    if (!ready || !this.personId) return null;
    return { id: this.personId, ...apiParams(filters, PERSON_PARAM_KEYS) };
  }

  async _maybeFetch() {
    const params = this._params();
    const key = params ? JSON.stringify(params) : null;
    if (key === this._requestKey) return;
    this._requestKey = key;
    if (!params) {
      this._data = null;
      return;
    }
    const { id, ...query } = params;
    this._loading = true;
    this._error = null;
    this._notFound = null;
    try {
      const data = await api.get(`/forecast/people/${id}`, query);
      if (this._requestKey !== key) return;
      this._data = data;
      this.dispatchEvent(new CustomEvent('person-data', { detail: data, bubbles: true }));
    } catch (err) {
      if (this._requestKey !== key) return;
      if (err.status === 404) this._notFound = err.message;
      else this._error = err.message;
      this._data = null;
    } finally {
      if (this._requestKey === key) this._loading = false;
    }
  }

  // -------------------------------------------------------------------------------------------
  // Model

  get _alloc() {
    return this._data?.allocation ?? null;
  }

  get _foldOf() {
    if (this._foldFor !== this._data) {
      this._fold = foldContributors(this._alloc?.contributors ?? []);
      this._foldFor = this._data;
    }
    return this._fold;
  }

  get _segments() {
    return foldSegments(this._foldOf);
  }

  _tokenOf(key) {
    const fold = this._foldOf;
    if (fold.nonproject?.key === key) return fold.nonproject.token;
    return fold.drawn.find((c) => c.key === key)?.token ?? fold.other?.token ?? null;
  }

  // -------------------------------------------------------------------------------------------
  // The allocation chart

  _capacityPath(weeks, x, y) {
    if (!weeks.length) return '';
    // Capacity is a weekly figure, so the line steps: flat across each band, joined at the edges.
    const parts = weeks.map((w) => {
      const x0 = x(w.week_start);
      const x1 = x0 + x.bandwidth();
      return `${x0},${y(w.capacity_hours)} L${x1},${y(w.capacity_hours)}`;
    });
    return `M${parts.join(' L')}`;
  }

  _allocationMarks = ({ x, y, plot, svg: s }) => {
    const weeks = this._alloc?.weeks ?? [];
    const fold = this._foldOf;
    const stacks = new Map(weeks.map((w) => [w.week_start, weekStack(w, fold)]));
    const segments = this._segments;
    return s`
      <g class="contributors">
        ${repeat(
          segments,
          (seg) => seg.key,
          (seg, i) => s`
            <g class="contributor" data-contributor=${seg.key}>
              ${weeks.map((w) => {
                const part = stacks.get(w.week_start)[i];
                if (!part || part.hours <= 0) return nothing;
                return s`<rect class="seg" data-week=${w.week_start}
                              x=${x(w.week_start)} width=${x.bandwidth()}
                              y=${y(part.to)} height=${Math.max(0, y(part.from) - y(part.to))}
                              fill=${fillFor(seg.token, seg.fill)} stroke="var(--surface)" stroke-width="1" />`;
              })}
            </g>`,
        )}
      </g>
      <path class="capacity" d=${this._capacityPath(weeks, x, y)} fill="none"
            stroke="var(--ink-strong)" stroke-width="2" stroke-dasharray="5 3" stroke-linejoin="round" />
      ${
        this._hover !== null && weeks[this._hover]
          ? s`<line class="crosshair" x1=${x.centre(weeks[this._hover].week_start)}
                    x2=${x.centre(weeks[this._hover].week_start)} y1=${plot.top} y2=${plot.top + plot.height}
                    stroke="var(--ink-muted)" stroke-width="1" stroke-dasharray="2 2" pointer-events="none" />`
          : nothing
      }
      <g class="hits">
        ${weeks.map(
          (w, i) => s`
            <rect class="hit" x=${x(w.week_start) - (x.step() - x.bandwidth()) / 2} width=${x.step()}
                  y=${plot.top} height=${plot.height} fill="transparent" style="cursor: crosshair"
                  @pointermove=${(e) => this._showTooltip(e, i)}
                  @pointerleave=${() => this._hideTooltip()}
                  @focus=${(e) => this._showTooltip(e, i)}
                  @blur=${() => this._hideTooltip()}
                  tabindex="0" role="img"
                  aria-label=${`Week of ${weekLabel(w.week_start)}: ${formatHours(w.allocated_hours)} allocated of ${formatHours(w.capacity_hours)}`} />`,
        )}
      </g>
    `;
  };

  _showTooltip(event, index) {
    const w = this._alloc?.weeks?.[index];
    if (!w) return;
    this._hover = index;
    const tooltip = this.renderRoot.querySelector('chart-tooltip[data-chart="allocation"]');
    if (!tooltip) return;
    const rect = event.currentTarget?.getBoundingClientRect?.();
    const rows = [...weekStack(w, this._foldOf)]
      .reverse()
      .filter((seg) => seg.hours > 0)
      .map((seg) => ({
        label: seg.contributors ? `${seg.label} (${count(seg.contributors.length)})` : seg.label,
        value: exact(seg.hours),
        token: seg.token,
        fill: seg.fill,
      }));
    rows.push({ label: 'Allocated', value: exact(w.allocated_hours) });
    rows.push({
      label: denominatorLabel(this._alloc.denominator),
      value: exact(w.capacity_hours),
    });
    const state = STATE_META.get(w.band);
    tooltip.show({
      x: event.clientX ?? (rect ? rect.left + rect.width / 2 : 0),
      y: event.clientY ?? (rect ? rect.top : 0),
      title: `Week of ${longDate(w.week_start)}`,
      rows,
      footer:
        w.utilization === null
          ? 'No capacity this week'
          : `${percent(w.utilization)} allocated${state ? ` · ${state.icon} ${state.label}` : ''}`,
    });
  }

  _hideTooltip() {
    this._hover = null;
    this.renderRoot.querySelector('chart-tooltip[data-chart="allocation"]')?.hide();
  }

  // -------------------------------------------------------------------------------------------
  // The billable chart: Workamajig's metric, its own axis, neutral ink

  _billableMarks = ({ x, y, plot, svg: s }) => {
    const b = this._data?.billable_utilization;
    const weeks = b?.weeks ?? [];
    const band = b?.healthy_band;
    return s`
      ${
        band
          ? s`<rect class="band" x=${plot.left} width=${plot.width}
                    y=${y(band.high)} height=${Math.max(0, y(band.low) - y(band.high))}
                    fill="var(--surface-sunken)" stroke="var(--border-soft)" stroke-width="1" />`
          : nothing
      }
      <g class="billable">
        ${weeks.map((w) =>
          w.utilization === null
            ? nothing
            : s`<rect class="bar" data-week=${w.week_start}
                      x=${x(w.week_start)} width=${x.bandwidth()}
                      y=${y(w.utilization)} height=${Math.max(0, y(0) - y(w.utilization))}
                      fill="var(--ink-muted)" stroke="var(--surface)" stroke-width="1" />`,
        )}
      </g>
      ${
        this._billableHover !== null && weeks[this._billableHover]
          ? s`<line class="crosshair" x1=${x.centre(weeks[this._billableHover].week_start)}
                    x2=${x.centre(weeks[this._billableHover].week_start)} y1=${plot.top} y2=${plot.top + plot.height}
                    stroke="var(--ink-muted)" stroke-width="1" stroke-dasharray="2 2" pointer-events="none" />`
          : nothing
      }
      <g class="hits">
        ${weeks.map(
          (w, i) => s`
            <rect class="hit" x=${x(w.week_start) - (x.step() - x.bandwidth()) / 2} width=${x.step()}
                  y=${plot.top} height=${plot.height} fill="transparent" style="cursor: crosshair"
                  @pointermove=${(e) => this._showBillableTooltip(e, i)}
                  @pointerleave=${() => this._hideBillableTooltip()}
                  @focus=${(e) => this._showBillableTooltip(e, i)}
                  @blur=${() => this._hideBillableTooltip()}
                  tabindex="0" role="img"
                  aria-label=${`Week of ${weekLabel(w.week_start)}: ${
                    w.utilization === null ? 'not reported' : `${percent(w.utilization)} billable`
                  }`} />`,
        )}
      </g>
    `;
  };

  _showBillableTooltip(event, index) {
    const b = this._data?.billable_utilization;
    const w = b?.weeks?.[index];
    if (!w) return;
    this._billableHover = index;
    const tooltip = this.renderRoot.querySelector('chart-tooltip[data-chart="billable"]');
    if (!tooltip) return;
    const rect = event.currentTarget?.getBoundingClientRect?.();
    const rows =
      w.billable_hours === null
        ? [{ label: 'Workamajig', value: 'not reported for this week' }]
        : [
            { label: 'Billable (Workamajig)', value: exact(w.billable_hours) },
            { label: 'Non-billable (Workamajig)', value: exact(w.nonbillable_hours) },
            { label: 'Logged (Workamajig)', value: exact(w.total_logged_hours) },
            { label: 'Net hours (this tool)', value: exact(w.net_hours) },
          ];
    const state = billableState(w.utilization, b.healthy_band);
    tooltip.show({
      x: event.clientX ?? (rect ? rect.left + rect.width / 2 : 0),
      y: event.clientY ?? (rect ? rect.top : 0),
      title: `Week of ${longDate(w.week_start)}`,
      rows,
      footer:
        w.utilization === null
          ? 'No billable utilization to show'
          : `${percent(w.utilization)} billable · ${billableStateLabel(state)} · reference only`,
    });
  }

  _hideBillableTooltip() {
    this._billableHover = null;
    this.renderRoot.querySelector('chart-tooltip[data-chart="billable"]')?.hide();
  }

  // -------------------------------------------------------------------------------------------
  // Blocks

  _tierChip(tier) {
    const meta = TIER_META.get(tier);
    if (!meta) return nothing;
    return html`<span class="chip" data-tier=${tier}
      >${meta.marker ? html`<span class="marker">${meta.marker}</span>` : nothing}
      ${meta.label}</span
    >`;
  }

  _capacityBlock(d) {
    const a = d.allocation;
    const t = a.totals;
    const off = t.pto_hours + t.holiday_hours + t.reduced_hours;
    const live = d.weeks ?? [];
    const offRows = live.flatMap((w) =>
      (w.exceptions ?? []).map((e) => ({ ...e, week_start: w.week_start })),
    );
    const sourceWords = {
      google_calendar: 'Master Calendar',
      manual: 'entered by a planner',
      workamajig: 'Workamajig',
    };
    const typeWords = { holiday: 'Holiday', pto: 'PTO', reduced_hours: 'Reduced hours' };
    return html`
      <section class="block" data-block="capacity">
        <h3>Capacity</h3>
        <ul class="cap-list">
          <li>
            <span>Contracted</span>
            <span class="figure" data-field="gross">${formatHours(t.gross_hours)}</span>
          </li>
          <li>
            <span>Time off (PTO, holidays, reduced hours)</span>
            <span class="figure" data-field="off">− ${formatHours(off)}</span>
          </li>
          <li>
            <span>Net hours at work</span>
            <span class="figure" data-field="net">${formatHours(t.net_hours)}</span>
          </li>
          <li>
            <span
              >Non-project
              load${
                a.denominator === 'net_hours' ? ' (a layer of the stack in this mode)' : ''
              }</span
            >
            <span class="figure" data-field="nonproject"
              >${a.denominator === 'net_hours' ? '' : '− '}${formatHours(t.nonproject_hours)}</span
            >
          </li>
          <li class="total">
            <span>${denominatorLabel(a.denominator)} over ${count(a.weeks_count)} weeks</span>
            <span class="figure" data-field="denominator">${formatHours(t.capacity_hours)}</span>
          </li>
        </ul>
        <p class="muted" data-field="time-off">${timeOffSentence(a.weeks, fmt)}</p>
        ${
          offRows.length
            ? html`<ul class="off-list plain-list" data-field="exceptions">
                ${offRows
                  .slice(0, 8)
                  .map(
                    (e) =>
                      html`<li>
                        ${typeWords[e.type] ?? humanize(e.type)} ${formatHours(e.hours)} in the week
                        of ${mediumDate(e.week_start)}
                        (${
                          e.date_from === e.date_to
                            ? mediumDate(e.date_from)
                            : `${mediumDate(e.date_from)} – ${mediumDate(e.date_to)}`
                        },
                        ${sourceWords[e.source] ?? e.source})
                      </li>`,
                  )}
                ${
                  offRows.length > 8
                    ? html`<li>… and ${count(offRows.length - 8)} more in the table.</li>`
                    : nothing
                }
              </ul>`
            : nothing
        }
        <p class="muted" data-field="capacity-note">
          The line in the chart is the capacity the run froze; the time off listed is today's model,
          which the next run will freeze.
        </p>
      </section>
    `;
  }

  _contributorsBlock(d) {
    const rows = d.allocation.contributors;
    return html`
      <section class="block" data-block="contributors">
        <h3>Contributing projects</h3>
        ${
          rows.length
            ? html`<ul class="contrib-list">
                ${repeat(
                  rows,
                  (c) => c.key,
                  (c) => html`
                    <li data-contributor=${c.key}>
                      <span class="name">
                        <span
                          class="swatch"
                          style=${`background: var(${this._tokenOf(c.key)})`}
                        ></span>
                        ${
                          c.entity_type === 'project' || c.entity_type === 'recurring'
                            ? html`<button
                                class="link label"
                                title=${`Open ${c.name} in Project Intelligence`}
                                @click=${() => navigate(`/projects/${c.entity_id}`)}
                              >
                                ${
                                  c.source_project_number ? `${c.source_project_number} ` : ''
                                }${c.name}
                              </button>`
                            : html`<span class="label">${c.name}</span>`
                        }
                        ${this._tierChip(c.confidence_tier)}
                      </span>
                      <span class="hours">${exact(c.hours)}<small>${percent(c.share)}</small></span>
                      <span class="detail">
                        ${humanize(c.source_mode)} · ${count(c.weeks_with_hours)}
                        ${c.weeks_with_hours === 1 ? 'week' : 'weeks'}${
                          c.first_week
                            ? `, ${weekLabel(c.first_week)} to ${weekLabel(c.last_week)}`
                            : ''
                        }
                      </span>
                    </li>
                  `,
                )}
              </ul>`
            : html`<p class="muted">The run put no hours on this person in the window.</p>`
        }
      </section>
    `;
  }

  _billableCard(d) {
    const b = d.billable_utilization;
    const state = billableState(b.totals.utilization, b.healthy_band);
    const basis = basisSentence(b);
    return html`
      <div class="card" data-card="billable">
        <div class="head">
          <div>
            <div class="eyebrow">
              Workamajig billable utilization <span class="reference">reference</span>
            </div>
            <h2>What share of the hours logged did Workamajig count as billable?</h2>
            <div class="sub">
              ${count(b.window.weeks)} complete weeks, ${weekLabel(b.window.from)} to
              ${weekLabel(b.window.to)} · Workamajig's healthy band ${percent(b.healthy_band.low)}
              to ${percent(b.healthy_band.high)} · a different metric from allocation, on its own
              scale
            </div>
          </div>
          <div class="kpis">
            <div class="kpi" data-kpi="billable">
              <div class="label">Billable</div>
              <div class="value">
                ${b.totals.utilization === null ? '—' : percent(b.totals.utilization)}
                ${
                  state
                    ? html`<small data-billable-state=${state}>${billableStateLabel(state)}</small>`
                    : nothing
                }
              </div>
            </div>
            <div class="kpi" data-kpi="billable-hours">
              <div class="label">Billable hours</div>
              <div class="value">
                ${formatHours(b.totals.billable_hours)}
                <small>of ${formatHours(b.totals.total_logged_hours)} logged</small>
              </div>
            </div>
          </div>
        </div>
        ${
          b.weeks.length && b.totals.weeks_reported
            ? html`
                <div class="chart">
                  <chart-frame
                    data-chart="billable"
                    .categories=${b.weeks.map((w) => w.week_start)}
                    .max=${billableScaleMax(b.weeks, b.healthy_band)}
                    .marks=${this._billableMarks}
                    .formatCategory=${(c) => weekLabel(c)}
                    .formatValue=${(v) => percent(v)}
                    value-label="Billable utilization"
                    category-label="Week commencing"
                    height="200"
                  ></chart-frame>
                  <div class="legend-row">
                    <span class="band-key"
                      ><span class="swatch"></span><span>Workamajig's healthy band</span></span
                    >
                    <span
                      >Bars: Workamajig's billable hours over this tool's net hours for the
                      week</span
                    >
                  </div>
                </div>
              `
            : nothing
        }
        <p class="muted" data-field="billable-sentence">${billableSentence(b, fmt)}</p>
        ${basis ? html`<p class="note" data-field="basis">${basis}</p>` : nothing}
        ${this._table(
          'billable weeks',
          this._billableOpen,
          (e) => (this._billableOpen = e.target.open),
          `Workamajig's billable utilization per week, reference only — ${d.person.display_name}`,
          'person-billable',
          this._billableColumns,
          billableExportRows(b),
          13,
        )}
        <chart-tooltip data-chart="billable"></chart-tooltip>
      </div>
    `;
  }

  // -------------------------------------------------------------------------------------------
  // Tables

  get _weekColumns() {
    // The specification is shared with the server's CSV (lib/person.js); the drawing is here.
    return withFormats(personWeekColumns({ denominator: this._alloc?.denominator }), {
      byUnit: {
        hours: (v) => exact(v),
        percent: (v) => (v === null || v === undefined ? '—' : percent(v)),
        week: (v) => weekLabel(v),
      },
      byKey: {
        band: (v) => (v ? html`<status-badge state=${v} compact></status-badge>` : '—'),
      },
    });
  }

  get _contributorColumns() {
    return withFormats(personContributorColumns(), {
      byUnit: {
        hours: (v) => exact(v),
        percent: (v) => percent(v),
        week: (v) => (v ? weekLabel(v) : '—'),
      },
    });
  }

  get _billableColumns() {
    const opt = (v) => (v === null || v === undefined ? '—' : exact(v));
    return withFormats(personBillableColumns(), {
      byUnit: {
        hours: opt,
        percent: (v) => (v === null || v === undefined ? '—' : percent(v)),
        week: (v) => weekLabel(v),
      },
      byKey: {
        net_hours: (v) => exact(v),
        gross_hours: (v) => exact(v),
        pto_hours: (v) => exact(v),
        holiday_hours: (v) => exact(v),
        state: (v) => billableStateLabel(v) ?? '—',
      },
    });
  }

  _table(name, open, onToggle, caption, exportName, columns, rows, pageSize = 25) {
    return html`
      <details class="table" ?open=${open} @toggle=${onToggle}>
        <summary>Table view: ${name}</summary>
        <data-table
          caption=${caption}
          export-name=${exportName}
          page-size=${pageSize}
          dense
          .columns=${columns}
          .rows=${rows}
        ></data-table>
      </details>
    `;
  }

  // -------------------------------------------------------------------------------------------
  // Render

  _subtitle(d) {
    const p = d.person;
    const parts = [p.title, p.department, p.office].filter(Boolean);
    const primary = (p.disciplines ?? []).find((x) => x.proficiency === 'primary');
    if (primary) parts.push(`${primary.name} (primary discipline)`);
    if (!p.is_active) parts.push('inactive');
    return parts.join(' · ');
  }

  _allocationSubtitle(a) {
    return `${count(a.weeks_count)} weeks from ${weekLabel(a.from)} · pipeline ${a.pipeline_mode} · ${
      a.denominator === 'net_hours'
        ? 'non-project load is a layer of the stack, and the line is net hours'
        : 'non-project load is taken from capacity, and the line is available hours'
    }`;
  }

  render() {
    const d = this._data;
    const { run } = this.appState;
    const title = d ? d.person.display_name : 'Individual view';

    let body;
    if (!this.personId) body = html`<div class="state">Pick a person to see their weeks.</div>`;
    else if (this._notFound)
      body = html`<div class="state" data-state="not-found">
        This person is not one you can see, or is not known here.
        <button class="link" @click=${() => navigate('/people')}>Pick another person</button>.
      </div>`;
    else if (this._error) body = html`<div class="state error">${this._error}</div>`;
    else if (!d && this._loading) body = html`<div class="state">Reading the person…</div>`;
    else if (!d) body = html`<div class="state">Nothing to show.</div>`;
    else if (!d.allocation)
      body = html`<div class="state" data-state="no-run">
        No forecast run yet — nothing to draw. Workamajig's billable utilization is below.
      </div>`;
    else {
      const a = d.allocation;
      const weeks = a.weeks;
      body = html`
        <p class="sub" data-field="allocation-line">${this._allocationSubtitle(a)}</p>
        ${
          weeks.length
            ? html`
                <div class="chart">
                  <chart-frame
                    data-chart="allocation"
                    .categories=${weeks.map((w) => w.week_start)}
                    .max=${allocationScaleMax(weeks)}
                    .marks=${this._allocationMarks}
                    .formatCategory=${(c) => weekLabel(c)}
                    value-label="Hours"
                    category-label="Week commencing"
                    height="280"
                  ></chart-frame>
                  <div class="legend-row">
                    <chart-legend
                      .items=${[...this._segments].reverse().map((seg) => ({
                        label: seg.contributors
                          ? `${seg.label} (${count(seg.contributors.length)})`
                          : seg.label,
                        token: seg.token,
                        fill: seg.fill,
                      }))}
                    ></chart-legend>
                    <span class="line-key">
                      <svg width="26" height="8" aria-hidden="true">
                        <line
                          x1="0"
                          x2="26"
                          y1="4"
                          y2="4"
                          stroke="var(--ink-strong)"
                          stroke-width="2"
                          stroke-dasharray="5 3"
                        />
                      </svg>
                      <span>${denominatorLabel(a.denominator)}</span>
                    </span>
                  </div>
                </div>
              `
            : html`<p class="note" data-note="no-weeks">No weeks in this window.</p>`
        }
        <div class="grid">${this._capacityBlock(d)} ${this._contributorsBlock(d)}</div>
        ${this._table(
          'weeks',
          this._weeksOpen,
          (e) => (this._weeksOpen = e.target.open),
          `Allocation per week against capacity — ${d.person.display_name}`,
          'person-weeks',
          this._weekColumns,
          allocationExportRows(a),
          26,
        )}
        ${this._table(
          'contributors',
          this._contributorsOpen,
          (e) => (this._contributorsOpen = e.target.open),
          `What put hours on this person in the window — ${d.person.display_name}`,
          'person-projects',
          this._contributorColumns,
          contributorExportRows(a),
        )}
      `;
    }

    const t = d?.allocation?.totals;
    return html`
      <div class="card" data-card="allocation">
        <div class="head">
          <div>
            <div class="eyebrow">
              Individual view${run ? ` · run of ${mediumDate(run.planning_date)}` : ''}
            </div>
            <h2>${title}</h2>
            ${d ? html`<div class="sub">${this._subtitle(d)}</div>` : nothing}
          </div>
          ${
            t
              ? html`<div class="kpis">
                  <div class="kpi" data-kpi="allocation">
                    <div class="label">Allocation</div>
                    <div class="value">
                      ${t.utilization === null ? '—' : percent(t.utilization)}
                      <small>over the window</small>
                    </div>
                  </div>
                  <div class="kpi" data-kpi="peak">
                    <div class="label">Peak</div>
                    <div class="value">
                      ${t.peak ? percent(t.peak.utilization) : '—'}
                      ${
                        t.peak
                          ? html`<small>week of ${weekLabel(t.peak.week_start)}</small>`
                          : nothing
                      }
                    </div>
                  </div>
                  <div class="kpi" data-kpi="over">
                    <div class="label">Over the line</div>
                    <div class="value">
                      ${count(t.weeks_over)}
                      <small>of ${count(d.allocation.weeks_count)} weeks</small>
                    </div>
                  </div>
                  <div class="kpi" data-kpi="time-off">
                    <div class="label">Time off</div>
                    <div class="value">
                      ${formatHours(t.pto_hours + t.holiday_hours + t.reduced_hours)}
                    </div>
                  </div>
                </div>`
              : nothing
          }
        </div>
        ${body}
        <chart-tooltip data-chart="allocation"></chart-tooltip>
      </div>
      ${d ? this._billableCard(d) : nothing}
    `;
  }
}

customElements.define('person-view', PersonView);
